export interface Snake {
  head: number;
  tail: number;
  label: string;
  description: string;
}

export interface Ladder {
  bottom: number;
  top: number;
  label: string;
  description: string;
}

export interface IncidentCard {
  id: string;
  square: number;
  scenario: string;
  question: string;
  options: string[];
  correctIndex: number;
  explanation: string;
  reward: number;
  penalty: number;
}

export const SNAKES: Snake[] = [
  { head: 98, tail: 78, label: "Cascading Failure", description: "Deployed without circuit breakers — cascading failure took down 3 services." },
  { head: 95, tail: 56, label: "No Rollback Plan", description: "Pushed a breaking change with no rollback strategy. Spent 4 hours reverting manually." },
  { head: 87, tail: 24, label: "Alert Fatigue", description: "Ignored 200 low-priority alerts. The one real incident was buried in the noise." },
  { head: 62, tail: 19, label: "Missing Runbooks", description: "On-call engineer had no runbook. Incident resolution took 3x longer than necessary." },
  { head: 54, tail: 34, label: "Untested Backups", description: "Backup existed but had never been tested. Restore failed during the actual incident." },
  { head: 47, tail: 16, label: "Config Drift", description: "Production config diverged from staging. Bug only appeared in prod." },
  { head: 35, tail: 11, label: "No Observability", description: "No metrics, no traces, no logs. Debugging was pure guesswork." },
];

export const LADDERS: Ladder[] = [
  { bottom: 4, top: 14, label: "Automated Testing", description: "Comprehensive test suite caught the regression before it hit production." },
  { bottom: 9, top: 31, label: "Feature Flags", description: "Used feature flags to safely roll out to 1% of users first." },
  { bottom: 20, top: 38, label: "Chaos Engineering", description: "Regular chaos experiments revealed the failure mode before it happened in prod." },
  { bottom: 28, top: 84, label: "SLO-Driven Alerts", description: "Alerts tied to SLOs — only paged when users were actually impacted." },
  { bottom: 40, top: 59, label: "Blameless Postmortems", description: "Ran a blameless postmortem. Found 3 systemic issues and fixed them all." },
  { bottom: 63, top: 81, label: "Canary Deployments", description: "Canary deployment caught the memory leak before full rollout." },
  { bottom: 71, top: 91, label: "Runbook Automation", description: "Automated runbook reduced MTTR from 45 minutes to 4 minutes." },
];

export const INCIDENT_CARDS: IncidentCard[] = [
  {
    id: "ic-01",
    square: 7,
    scenario: "Your service's p99 latency just spiked from 120ms to 4200ms. No recent deployments. CPU and memory look normal.",
    question: "What is your FIRST action?",
    options: [
      "Immediately restart all pods to clear the issue",
      "Check downstream dependencies and database query times",
      "Page the entire on-call team and declare a P1",
      "Roll back to the previous deployment anyway"
    ],
    correctIndex: 1,
    explanation: "When latency spikes without a deployment, the issue is almost always downstream — a slow DB query, a saturated dependency, or a network issue. Check your traces and dependency health first before taking disruptive action.",
    reward: 3,
    penalty: 2
  },
  {
    id: "ic-02",
    square: 13,
    scenario: "You're on-call. An alert fires: 'Error rate > 5% on /api/checkout'. It's 2am. You have no context on this service.",
    question: "What's the most effective first step?",
    options: [
      "Wake up the service owner immediately",
      "Open the runbook for this service and follow the triage steps",
      "Disable the endpoint to stop the errors",
      "Check if the alert has been firing for a while and ignore it"
    ],
    correctIndex: 1,
    explanation: "Runbooks exist precisely for this scenario. A good runbook gives you triage steps, escalation paths, and context — even at 2am with zero prior knowledge of the service. Always check the runbook first.",
    reward: 4,
    penalty: 2
  },
  {
    id: "ic-03",
    square: 22,
    scenario: "A database migration is running in production. Halfway through, you notice table locks are causing timeouts across 6 services.",
    question: "What should you do?",
    options: [
      "Let it finish — stopping mid-migration is worse",
      "Kill the migration immediately and restore from backup",
      "Assess impact, communicate to stakeholders, and decide based on blast radius",
      "Restart the affected services to clear the locks"
    ],
    correctIndex: 2,
    explanation: "Incident response requires situational awareness. Assess the blast radius first — how many users are affected, how severe is the impact? Then communicate clearly before taking action. Killing or continuing both have tradeoffs that depend on context.",
    reward: 3,
    penalty: 3
  },
  {
    id: "ic-04",
    square: 33,
    scenario: "Your monitoring shows memory usage climbing steadily on your API servers. It's at 78% and growing 2% per hour.",
    question: "What is the correct response?",
    options: [
      "Wait until it hits 95% before acting — it might stabilize",
      "Immediately scale horizontally and investigate the memory leak in parallel",
      "Restart servers now to buy time, then investigate the root cause",
      "Increase memory limits and monitor"
    ],
    correctIndex: 1,
    explanation: "Scale first to protect availability, then investigate. Restarting buys time but doesn't fix the leak. Waiting until 95% risks an OOM crash. Increasing limits just delays the inevitable. The right move is to protect users while you find the root cause.",
    reward: 3,
    penalty: 2
  },
  {
    id: "ic-05",
    square: 44,
    scenario: "You've just resolved a major incident. The service is stable. Your manager asks for a postmortem.",
    question: "What makes a postmortem genuinely useful?",
    options: [
      "Identify who made the mistake and document it clearly",
      "Focus on timeline, contributing factors, and systemic action items — not blame",
      "Write a brief summary and move on quickly to avoid dwelling on failures",
      "List every alert that fired during the incident"
    ],
    correctIndex: 1,
    explanation: "Blameless postmortems focus on systems, not people. The goal is to find contributing factors and create action items that prevent recurrence. Blame creates a culture of fear and hides future incidents. Systemic thinking is what makes teams more reliable over time.",
    reward: 4,
    penalty: 2
  },
  {
    id: "ic-06",
    square: 51,
    scenario: "You're designing alerting for a new service. You want to be notified when something goes wrong.",
    question: "Which alerting strategy is most effective?",
    options: [
      "Alert on every error log entry to catch issues early",
      "Alert on CPU > 80% and memory > 80% as leading indicators",
      "Alert on SLO burn rate — when error budget is being consumed too fast",
      "Alert on deployment events so you know when changes happen"
    ],
    correctIndex: 2,
    explanation: "SLO-based alerting is the gold standard. It tells you when users are actually being impacted, not just when a resource is high. CPU and memory alerts create noise. Error log alerts are too granular. Burn rate alerts give you actionable, user-centric signal.",
    reward: 5,
    penalty: 3
  },
  {
    id: "ic-07",
    square: 58,
    scenario: "A new engineer asks: 'Why do we need distributed tracing if we already have logs?'",
    question: "What's the best answer?",
    options: [
      "We don't — logs are sufficient for most debugging",
      "Traces show request flow across services, correlating latency and errors that logs alone can't connect",
      "Traces are only useful for microservices with more than 10 services",
      "Tracing is mainly for compliance and audit purposes"
    ],
    correctIndex: 1,
    explanation: "Logs tell you what happened on a single service. Traces tell you the full story of a request as it flows through multiple services. When a request touches 8 services and is slow, traces show you exactly where the time was spent — something logs alone cannot do.",
    reward: 3,
    penalty: 2
  },
  {
    id: "ic-08",
    square: 67,
    scenario: "Your team is debating whether to add a new feature flag or deploy directly to production.",
    question: "When should you use a feature flag?",
    options: [
      "Only for UI changes — backend changes don't need flags",
      "Never — feature flags add complexity and technical debt",
      "For any change that carries risk, especially those affecting critical paths or large user segments",
      "Only when the QA team requests it"
    ],
    correctIndex: 2,
    explanation: "Feature flags decouple deployment from release. They let you ship code safely, test with a subset of users, and kill a feature instantly if something goes wrong — without a rollback. They're especially valuable for high-risk changes on critical paths.",
    reward: 3,
    penalty: 2
  },
  {
    id: "ic-09",
    square: 76,
    scenario: "Your service has an SLA of 99.9% uptime. You've had 40 minutes of downtime this month.",
    question: "How much error budget do you have left for the month?",
    options: [
      "About 4 minutes",
      "About 44 minutes",
      "About 3 hours",
      "You've already exceeded your error budget"
    ],
    correctIndex: 0,
    explanation: "99.9% uptime = 43.8 minutes of allowed downtime per month. You've used 40 minutes, leaving roughly 3-4 minutes. You're nearly out of error budget — this should trigger a freeze on risky deployments and a focus on reliability work.",
    reward: 4,
    penalty: 3
  },
  {
    id: "ic-10",
    square: 83,
    scenario: "During a high-severity incident, your Slack channel has 15 engineers all suggesting different fixes simultaneously.",
    question: "What's the most important thing to do?",
    options: [
      "Try all suggestions in parallel to find the fix faster",
      "Assign an Incident Commander to coordinate, limit changes to one at a time",
      "Mute the channel and work alone to avoid confusion",
      "Ask everyone to vote on the best fix"
    ],
    correctIndex: 1,
    explanation: "Incident management requires clear command structure. An Incident Commander coordinates the response, limits simultaneous changes (which make root cause analysis impossible), and keeps communication clear. Chaos during incidents extends MTTR significantly.",
    reward: 5,
    penalty: 3
  },
  {
    id: "ic-11",
    square: 89,
    scenario: "You're doing capacity planning for a service that handles 10,000 RPS at peak. You want to ensure reliability.",
    question: "What's the right approach?",
    options: [
      "Provision exactly for peak load — no need to over-provision",
      "Provision for 2x peak load and implement auto-scaling with load shedding",
      "Use spot instances to save cost and accept occasional failures",
      "Only scale when you start seeing errors in production"
    ],
    correctIndex: 1,
    explanation: "Reliable systems are provisioned with headroom. 2x peak gives you buffer for traffic spikes, rolling deployments, and node failures. Auto-scaling handles growth. Load shedding protects the system when capacity is exceeded. Reactive scaling is too slow for real traffic spikes.",
    reward: 4,
    penalty: 3
  },
  {
    id: "ic-12",
    square: 93,
    scenario: "You're reviewing a PR that removes a health check endpoint from a critical service.",
    question: "What's your response?",
    options: [
      "Approve it — health checks are just overhead",
      "Block it — health checks are essential for load balancer routing and deployment safety",
      "Approve with a comment to add it back later",
      "Ask the author to move it to a different endpoint path"
    ],
    correctIndex: 1,
    explanation: "Health check endpoints are critical infrastructure. Load balancers use them to route traffic. Deployment systems use them to verify rollout success. Kubernetes uses them for liveness and readiness probes. Removing them silently breaks your deployment pipeline and incident response capabilities.",
    reward: 5,
    penalty: 4
  }
];

export const RANK_THRESHOLDS = [
  { min: 100, rank: "Production Guardian", icon: "ri-shield-star-line", color: "#00ff88" },
  { min: 80, rank: "Incident Commander", icon: "ri-command-line", color: "#00d9ff" },
  { min: 60, rank: "Reliability Engineer", icon: "ri-settings-3-line", color: "#00d9ff" },
  { min: 40, rank: "On-Call Survivor", icon: "ri-alarm-warning-line", color: "#f59e0b" },
  { min: 20, rank: "Page Receiver", icon: "ri-notification-3-line", color: "#f59e0b" },
  { min: 0, rank: "Incident Intern", icon: "ri-user-line", color: "#ff4444" },
];

export const INCIDENT_TIPS = [
  "Always check your SLO burn rate before declaring an incident resolved.",
  "The first 5 minutes of an incident define the next 2 hours. Stay calm and structured.",
  "Correlation is not causation. A recent deployment isn't always the cause.",
  "Good runbooks are written during calm times, not during incidents.",
  "Observability is not monitoring. Monitoring tells you something is wrong. Observability tells you why.",
  "Error budgets make reliability a business conversation, not just an engineering one.",
  "Blameless culture is not about avoiding accountability — it's about learning from systems.",
  "The best incident response is the one you practiced before the incident happened.",
  "Distributed tracing is your map. Without it, you're navigating a microservices maze blindfolded.",
  "Chaos engineering is controlled failure. It's better to break things on your terms.",
];
